import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { GridSkeleton } from "@/components/shared/Skeletons";
import {
  Award,
  BookOpen,
  Check,
  Clock,
  Flag,
  Flame,
  Heart,
  Medal,
  Plus,
  Shield,
  Star,
  Target,
  Trash2,
  TrendingUp,
  Trophy,
  Users,
  Zap,
} from "lucide-react";
import PageHeader from "@/components/shared/PageHeader";
import StatCard from "@/components/shared/StatCard";
import EmptyState from "@/components/shared/EmptyState";
import { BadgeGrid } from "@/components/shared/BadgeDisplay";
import { Field } from "@/components/forms";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  useAdminBadges,
  useCreateBadge,
  useDeleteBadge,
  DEFAULT_BADGES,
} from "@/hooks/useBadges";
import { toast } from "sonner";

export type Badge = {
  id?: number;
  name: string;
  description?: string;
  icon?: string;
  criteria_type?: string;
  criteria_value?: number;
};

export const Route = createFileRoute("/admin/badges/")({
  component: AdminBadgesPage,
});

const CRITERIA_TYPES = ["hours", "tips", "tasks", "rating", "milestone"];

const ICON_OPTIONS = [
  { value: "trophy", label: "Trophy", icon: Trophy },
  { value: "star", label: "Star", icon: Star },
  { value: "award", label: "Award", icon: Award },
  { value: "medal", label: "Medal", icon: Medal },
  { value: "clock", label: "Clock", icon: Clock },
  { value: "heart", label: "Heart", icon: Heart },
  { value: "flame", label: "Flame", icon: Flame },
  { value: "zap", label: "Zap", icon: Zap },
  { value: "shield", label: "Shield", icon: Shield },
  { value: "flag", label: "Flag", icon: Flag },
  { value: "target", label: "Target", icon: Target },
  { value: "book", label: "Book", icon: BookOpen },
  { value: "check", label: "Check", icon: Check },
  { value: "trending-up", label: "Trending Up", icon: TrendingUp },
  { value: "users", label: "Users", icon: Users },
];

function AdminBadgesPage() {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBadge, setSelectedBadge] = useState<Badge | null>(null);
  const [badgeName, setBadgeName] = useState("");
  const [badgeDesc, setBadgeDesc] = useState("");
  const [badgeIcon, setBadgeIcon] = useState("trophy");
  const [badgeCriteriaType, setBadgeCriteriaType] = useState("hours");
  const [badgeCriteriaValue, setBadgeCriteriaValue] = useState(10);
  const [filterType, setFilterType] = useState("all");

  const { data: badgesData, isLoading } = useAdminBadges();
  const badges: Badge[] = (badgesData ?? []) as Badge[];
  const createBadge = useCreateBadge();
  const deleteBadge = useDeleteBadge();

  const displayBadges =
    badges.length > 0
      ? badges
      : DEFAULT_BADGES;

  const filteredBadges =
    filterType === "all"
      ? displayBadges
      : displayBadges.filter((b) => b.criteria_type === filterType);

  const handleCreateBadge = async () => {
    if (!badgeName.trim()) {
      toast.error("Badge name is required");
      return;
    }
    try {
      await createBadge.mutateAsync({
        name: badgeName.trim(),
        description: badgeDesc.trim(),
        icon: badgeIcon,
        criteria_type: badgeCriteriaType,
        criteria_value: badgeCriteriaValue,
      });
      toast.success("Badge created successfully");
      setCreateDialogOpen(false);
      setBadgeName("");
      setBadgeDesc("");
      setBadgeIcon("trophy");
      setBadgeCriteriaType("hours");
      setBadgeCriteriaValue(10);
    } catch (err) {
      console.error("Failed to create badge:", err);
    }
  };

  const handleDeleteBadge = async () => {
    if (!selectedBadge || !selectedBadge.id) return;
    try {
      await deleteBadge.mutateAsync(selectedBadge.id);
      toast.success(`Badge "${selectedBadge.name}" deleted`);
      setDeleteDialogOpen(false);
      setSelectedBadge(null);
    } catch (err) {
      console.error("Failed to delete badge:", err);
    }
  };

  const totalBadges = displayBadges.length;
  const capitalize = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Badge management"
        subtitle="Create and manage badges for student achievements and gamification."
        breadcrumbs={[
          { label: "Dashboard", path: "/admin/dashboard" },
          { label: "Badges" },
        ]}
        action={
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus />
            Create badge
          </Button>
        }
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 md:gap-4">
        <StatCard label="Total badges" value={totalBadges} icon={<Trophy />} />
        <StatCard
          label="Hours badges"
          value={displayBadges.filter((b) => b.criteria_type === "hours").length}
          icon={<Clock />}
        />
        <StatCard
          label="Tips badges"
          value={displayBadges.filter((b: any) => b.criteria_type === "tips").length}
          icon={<Heart />}
        />
      </div>

      <Tabs value={filterType} onValueChange={setFilterType}>
        <TabsList variant="segmented" aria-label="Filter by criteria" className="max-w-full overflow-x-auto">
          <TabsTrigger value="all">All</TabsTrigger>
          {CRITERIA_TYPES.map((type) => (
            <TabsTrigger key={type} value={type}>
              {capitalize(type)}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {isLoading ? (
        <GridSkeleton cards={6} />
      ) : (
        <BadgeGrid
          badges={filteredBadges as Badge[]}
          size="lg"
          showDescription
          earnedBadgeIds={[]}
          cols={{ xs: 12, sm: 6, md: 4, lg: 3 }}
          onBadgeClick={(badge) => {
            setSelectedBadge(badge);
            setDeleteDialogOpen(true);
          }}
        />
      )}

      {filteredBadges.length === 0 && !isLoading && (
        <EmptyState
          icon={<Trophy />}
          title="No badges yet"
          description="Create your first badge to start rewarding students."
        />
      )}

      <Dialog open={createDialogOpen} onOpenChange={(o) => !o && setCreateDialogOpen(false)}>
        <DialogContent className="max-h-[calc(100dvh-2rem)] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create new badge</DialogTitle>
            <DialogDescription>Badges are awarded automatically when a student reaches the target.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <Field label="Badge icon" hint="Choose an icon that represents the achievement">
              {({ id, describedBy }) => (
                <Select value={badgeIcon} onValueChange={setBadgeIcon}>
                  <SelectTrigger id={id} aria-describedby={describedBy}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ICON_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <span className="flex items-center gap-2">
                          <opt.icon className="size-3.5 text-muted-foreground" aria-hidden />
                          {opt.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>

            <Field label="Badge name" required>
              <Input
                value={badgeName}
                onChange={(e) => setBadgeName(e.target.value)}
                placeholder="e.g. 100 Hours"
              />
            </Field>

            <Field label="Description">
              <Textarea
                rows={2}
                value={badgeDesc}
                onChange={(e) => setBadgeDesc(e.target.value)}
                placeholder="What does the student earn this badge for?"
              />
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Criteria type">
                {({ id }) => (
                  <Select value={badgeCriteriaType} onValueChange={setBadgeCriteriaType}>
                    <SelectTrigger id={id}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CRITERIA_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>
                          {capitalize(type)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              <Field label="Target value" hint={`e.g. 50 means ${badgeCriteriaType} >= 50`}>
                <Input
                  type="number"
                  min={1}
                  value={badgeCriteriaValue}
                  onChange={(e) => setBadgeCriteriaValue(Number(e.target.value))}
                  className="tabular-nums"
                />
              </Field>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateBadge}
              loading={createBadge.isPending}
              disabled={createBadge.isPending || !badgeName.trim()}
            >
              {createBadge.isPending ? "Creating…" : "Create badge"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={(o) => !o && setDeleteDialogOpen(false)}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete badge</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this badge? Students who have already earned it will keep their records.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {selectedBadge && (
            <div className="flex items-center gap-3 rounded-md border border-border bg-background p-3">
              <span
                className="flex size-9 shrink-0 items-center justify-center rounded-sm border border-border text-body"
                aria-hidden
              >
                <Trophy className="size-4" />
              </span>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-foreground">{selectedBadge.name}</p>
                <p className="text-caption text-muted-foreground">{selectedBadge.description}</p>
              </div>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              onClick={handleDeleteBadge}
              loading={deleteBadge.isPending}
            >
              {!deleteBadge.isPending && <Trash2 />}
              {deleteBadge.isPending ? "Deleting…" : "Delete badge"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
